/**
 * MetricSelector Component (Story 5.34, Task 2)
 *
 * Renders a metric dropdown grouped by category with compatibility filtering.
 * For multi-metric widgets (line-chart, data-table), shows an "Add Metric" flow.
 * Incompatible metrics are disabled with a title tooltip.
 *
 * Story 37.7: compatibility is now the DUAL-AXIS rule — a metric that declares
 * `compatibleWidgets` is filtered against the widget TYPE (the authoritative
 * axis); a metric without it falls back to the category-allowlist axis (the
 * `compatibility` prop). `groupMetricsByCategory` is exported so the picker /
 * drift surfaces can reuse it instead of duplicating it (correction #13).
 * Deprecated metrics are greyed/disabled with a `replacedBy` hint (AC #7).
 */

import type { MetricDefinition } from "@provenleap/integrations";
import { useMemo } from "react";
import { isMetricCompatibleWithWidget } from "../../lib/metricCompatibility";
import { SearchableSelect, type SearchableSelectGroup } from "./SearchableSelect";

interface MetricSelectorProps {
  widgetType: string;
  metrics: MetricDefinition[];
  selectedMetrics: Array<{ key: string; integrationId: string; label: string }>;
  isLoading: boolean;
  isMultiMetric: boolean;
  onSelect: (metricKey: string, metricLabel: string) => void;
  compatibility: string[];
}

/**
 * Group metrics by category for dropdown display (Story 37.7: exported).
 *
 * Compatibility is the SHARED dual-axis rule — this calls
 * `isMetricCompatibleWithWidget` (the single source of truth used by the
 * auto-bind resolver too), so the picker filter and the binding can never
 * diverge (Round-1 review finding #1). When a `widgetType` is given, the helper
 * evaluates axis 1 (`compatibleWidgets`) then axis 2 (category allowlist). When
 * NO `widgetType` is given (legacy callers that only pass `compatibility`), we
 * fall back to the category-allowlist check directly so behaviour is unchanged.
 * Deprecated metrics are flagged so the UI can grey them out + surface a
 * `replacedBy` hint.
 */
export function groupMetricsByCategory(
  metrics: MetricDefinition[],
  compatibility: string[],
  widgetType?: string,
): Array<{ category: string; metrics: Array<MetricDefinition & { compatible: boolean }> }> {
  const groups: Record<string, Array<MetricDefinition & { compatible: boolean }>> = {};

  for (const metric of metrics) {
    const category = metric.category ?? "Other";
    if (!groups[category]) groups[category] = [];

    let isCompatible: boolean;
    if (widgetType) {
      // Single source of truth — the SAME dual-axis predicate the auto-bind
      // resolver uses (review finding #1: no second divergent filter).
      isCompatible = isMetricCompatibleWithWidget(metric, widgetType);
    } else if (metric.compatibleWidgets && metric.compatibleWidgets.length > 0) {
      // Legacy path (no widgetType): cannot evaluate axis 1 → treat a declared
      // metric as compatible rather than silently hiding it.
      isCompatible = true;
    } else {
      isCompatible =
        compatibility.includes("*") || compatibility.some((c) => c.toLowerCase() === category.toLowerCase());
    }

    groups[category].push({ ...metric, compatible: isCompatible });
  }

  // Sort categories alphabetically
  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, metrics]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      metrics: metrics.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

export function MetricSelector({
  widgetType,
  metrics,
  selectedMetrics,
  isLoading,
  isMultiMetric,
  onSelect,
  compatibility,
}: MetricSelectorProps) {
  const groupedMetrics = useMemo(
    () => groupMetricsByCategory(metrics, compatibility, widgetType),
    [metrics, compatibility, widgetType],
  );

  const selectedKey = selectedMetrics.length === 1 && !isMultiMetric ? selectedMetrics[0]!.key : "";

  // SearchableSelect groups — the custom popover owns search/filtering (matching
  // a metric label OR its category subtitle). We describe each row's label,
  // disabled state, deprecated/incompatible suffix + tooltip, and search text.
  const metricGroups = useMemo<SearchableSelectGroup[]>(() => {
    return groupedMetrics.map((group) => ({
      key: group.category,
      label: group.category,
      options: group.metrics.map((metric) => {
        // Story 37.7 AC #7: deprecated metrics are greyed/disabled with a
        // `replacedBy` hint; a metric can be BOTH deprecated and incompatible.
        const isDeprecated = metric.deprecated === true;
        const isIncompatible = !metric.compatible;
        // Never disable the CURRENTLY-SELECTED option (review R2-7).
        const isSelectedOpt = !isMultiMetric && metric.key === selectedKey;
        const isDisabled = (isIncompatible || isDeprecated) && !isSelectedOpt;
        const hints: string[] = [];
        if (isDeprecated) {
          hints.push(
            metric.replacedBy
              ? `${metric.label} is deprecated — use ${metric.replacedBy} instead`
              : `${metric.label} is deprecated`,
          );
        }
        if (isIncompatible) hints.push(`${metric.label} is not compatible with ${widgetType} widgets`);
        const suffix = [isDeprecated ? "deprecated" : null, isIncompatible ? "incompatible" : null]
          .filter(Boolean)
          .join(", ");
        return {
          // Key by category+key: the same metric key can appear under two
          // categories, which would collide on a bare metric.key (R2-6) — but
          // SearchableSelect keys options by value, so we keep value = metric.key
          // (a metric is selected by key regardless of which category it sits in).
          value: metric.key,
          searchText: metric.label,
          disabled: isDisabled,
          title: hints.length > 0 ? hints.join("; ") : undefined,
          content: (
            <span className="truncate">
              {metric.label}
              {suffix ? ` (${suffix})` : ""}
            </span>
          ),
        };
      }),
    }));
  }, [groupedMetrics, isMultiMetric, selectedKey, widgetType]);

  // Review #10 + R2-5 + R3-7: surface a banner listing EVERY currently-bound
  // deprecated metric — for single-metric AND multi-metric widgets — so a saved
  // widget bound to since-deprecated metrics isn't silently left selected with
  // no explanation (and a multi-metric widget surfaces ALL of them, not just
  // the first). Scans every selected key against the available metrics.
  const deprecatedByKey = useMemo(() => {
    const map = new Map<string, MetricDefinition>();
    for (const m of metrics) if (m.deprecated === true) map.set(m.key, m);
    return map;
  }, [metrics]);
  const boundDeprecatedMetrics = useMemo(() => {
    const seen = new Set<string>();
    const out: MetricDefinition[] = [];
    for (const sm of selectedMetrics) {
      const dep = deprecatedByKey.get(sm.key);
      if (dep && !seen.has(dep.key)) {
        seen.add(dep.key);
        out.push(dep);
      }
    }
    return out;
  }, [selectedMetrics, deprecatedByKey]);

  if (isLoading) {
    return (
      <div data-testid="metric-selector" className="space-y-1">
        <label className="block text-xs font-medium text-foreground">Add metric</label>
        <div className="w-full px-1.5 py-1 text-xs text-muted-foreground">Loading metrics...</div>
      </div>
    );
  }

  return (
    <div data-testid="metric-selector" className="space-y-2">
      {/* Label-left + 130px control, matching the Appearance fields. */}
      <div className="grid grid-cols-[1fr_130px] items-center gap-2">
        <label className="min-w-0 text-[13px] text-foreground">Add metric</label>
        <SearchableSelect
          // Multi-metric is an "add" picker: keep it unselected (undefined) so a
          // re-pick of the same metric still fires; single-metric is controlled.
          value={isMultiMetric ? undefined : (selectedKey || undefined)}
          onChange={(v) => {
            const metric = metrics.find((m) => m.key === v);
            if (metric) onSelect(metric.key, metric.label);
          }}
          groups={metricGroups}
          placeholder="Metric"
          searchPlaceholder="Search metrics..."
          emptyText="No metrics found"
          testId="metric-select"
          searchTestId="metric-search"
          triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
        />
      </div>
      {boundDeprecatedMetrics.length > 0 && (
        <div data-testid="metric-deprecated-notice" role="status" className="text-[11px] text-destructive space-y-0.5">
          {boundDeprecatedMetrics.map((m) => (
            <p key={m.key}>
              “{m.label}” is deprecated
              {m.replacedBy ? ` — switch to ${m.replacedBy}` : ""}.
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
